<template>
  <div class="flex flex-col gap-100">
    <LcCard
      v-for="({ headline, text }, index) in cards"
      :key="index"
      body-class="px-100 py-050"
      class="bg-secondary-secondary-bold"
      header-class="px-100 pt-050 paragraph-bold"
      shadow="none"
    >
      <template #header>{{ headline }}</template>

      <i18n-t class="text-secondary-secondary" :keypath="text.keypath" tag="p">
        <template v-for="key in getKeys(text.values)" #[key]>
          <span
            v-if="text.values.hasOwnProperty(key)"
            :key="`${index}_${key}`"
            :class="text.values[key]?.class"
          >
            {{ text.values[key].value }}
          </span>
        </template>
      </i18n-t>
    </LcCard>
  </div>
</template>

<script lang="ts" setup>
const getKeys = <T extends object>(obj: T) => Object.keys(obj) as (keyof T)[]

const { t, te } = useI18n()
const { addOns, config, disabledAddOns, gameId, prizeTier } = useHighFrequencyGame()
const { formatPrizeMoney } = usePrizeMoneyFormatter()

const selectedAddOns = computed(
  () => prizeTier.value?.prizesPerAddOn.map(({ addOn }) => addOn) || [],
)

const inactiveAddOns = computed(() =>
  addOns.value.filter(
    (addOn) => !disabledAddOns.value.includes(addOn) && !selectedAddOns.value.includes(addOn),
  ),
)

const getMaxMultiplier = (name: string) =>
  config.value?.addOns.find((addOn) => addOn.name === name)?.maximumMultiplier ?? 0

const resolveHintKeypath = (state: 'active' | 'inactive', addOn: string) => {
  const perAddOnKeypath = `highfrequencygame.prizemodal.hint.${addOn}.${state}.text`
  return te(perAddOnKeypath) ? perAddOnKeypath : `highfrequencygame.prizemodal.hint.${state}.text`
}

const cards = computed(() => {
  const currentPrizeTier = prizeTier.value
  if (!currentPrizeTier) return []

  const inactiveAddOnCards = inactiveAddOns.value.map((addOn) => ({
    headline: t(`highfrequencygame.prizemodal.hint.inactive.headline`),
    text: {
      keypath: resolveHintKeypath('inactive', addOn),
      values: {
        addOn: {
          class: 'text-primary-primary',
          value: t(`highfrequencygame.addon.${addOn}`),
        },
        value: {
          class: 'text-primary-primary',
          value: t(`highfrequencygame.prizemodal.hint.inactive.text.multiplier`, {
            value: getMaxMultiplier(addOn),
          }),
        },
      },
    },
  }))

  const activeAddOnCards =
    currentPrizeTier.prizesPerAddOn.map(({ addOn, maximumPrize }) => ({
      headline: t(`highfrequencygame.prizemodal.hint.active.headline.${gameId}.${addOn}`),
      text: {
        keypath: resolveHintKeypath('active', addOn),
        values: {
          from: {
            value: formatPrizeMoney(currentPrizeTier.maximumPrizeWithoutAddOns),
          },
          to: {
            value: formatPrizeMoney(maximumPrize),
          },
          value: {
            class: 'text-primary-primary',
            value: t(`highfrequencygame.prizemodal.hint.active.text.multiplier`, {
              value: getMaxMultiplier(addOn),
            }),
          },
        },
      },
    })) ?? []

  return [...inactiveAddOnCards, ...activeAddOnCards]
})
</script>
